import React from 'react';
import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './Sidebar.css';
import { 
  Home,
  CheckSquare,
  Lightbulb,
  ListTodo,
  FolderKanban,
  Book,
  Notebook,
  Link2,
  Tag,
  MessageSquare,
  User2,
  ChevronDown,
  ChevronRight,
  GitFork,
  ChevronLeftCircle,
  ChevronRightCircle,
  Mail,
  MessageCircle,
  Plus,
  Inbox,
  FileText,
  Send,
  Trash2,
  FolderMinus
} from 'lucide-react';

interface SidebarProps {
  isCollapsed: boolean;
  onToggleCollapse: (collapsed: boolean) => void;
}

interface SidebarItem {
  id: string;
  label: string;
  icon?: any;
  count?: number;
}

// Basic sidebar items, Email will inject children dynamically
const baseSidebarItems: SidebarItem[] = [
  {
    id: 'home',
    label: 'Home',
    icon: <Home size={18} />,
  },
  // Ideas section
  { id: 'ideas', label: 'Ideas', icon: <Lightbulb size={18} /> },
  { id: 'decisions', label: 'Decisions', icon: <GitFork size={18} /> },
  
  { id: 'separator1', label: '', icon: null }, // Spacing
  
  // Task Management items
  { id: 'todo', label: 'ToDo', icon: <CheckSquare size={18} /> },
  { id: 'tasks', label: 'Tasks', icon: <ListTodo size={18} /> },
  { id: 'projects', label: 'Projects', icon: <FolderKanban size={18} /> },
  // { id: 'groups', label: 'Groups', icon: <User2 size={18} /> }, // Disabled: needs WebSocket support
  
  { id: 'separatorCommsTop', label: '', icon: null }, // Spacing before communications
  
  // Email header placeholder (children will be injected later)
  { id: 'email', label: 'Email', icon: <Mail size={18} /> },
  
  { id: 'chat', label: 'Chat', icon: <MessageCircle size={18} /> },
  
  { id: 'separatorCommsBottom', label: '', icon: null }, // Spacing after communications
  
  // Notes section
  { id: 'journal', label: 'Journal', icon: <Book size={18} /> },
  { id: 'notebooks', label: 'Notebooks', icon: <Notebook size={18} /> },
  { id: 'weblinks', label: 'Web Links', icon: <Link2 size={18} /> },
  
  { id: 'separator3', label: '', icon: null }, // Spacing
  
  // Bottom items
  { id: 'tags', label: 'Tags', icon: <Tag size={18} /> },
  { id: 'ai-chat', label: 'AI Chat', icon: <MessageSquare size={18} /> },
];

const Sidebar = ({ isCollapsed, onToggleCollapse }: SidebarProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  // map pathname to section id
  const pathToSection = (pathname: string): string => {
    if (pathname === '/' || pathname === '') return 'home';
    const segment = pathname.split('/')[1];
    return segment || 'home';
  };

  const activeSection = pathToSection(location.pathname);

  // Collapse sidebar on navigation for mobile
  useEffect(() => {
    if (isCollapsed && window.innerWidth <= 768) {
      // do nothing
    }
  }, [location.pathname, isCollapsed]);

  const handleItemClick = (itemId: string) => {
    navigate(itemId === 'home' ? '/' : `/${itemId}`);
  };

  return (
    <aside className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
      <div className="app-title">
        <span className="title-text">{isCollapsed ? 'S' : 'SuperTask'}</span>
      </div>
      <nav>
        <ul className="sidebar-sections">
          {baseSidebarItems.map(item => (
            <li key={item.id} className="sidebar-section">
              {item.id.startsWith('separator') ? (
                <div className="sidebar-separator" />
              ) : item.id === 'email' ? (
                <div
                  className={`sidebar-item ${activeSection.startsWith('email') ? 'active' : ''}`}
                  onClick={() => handleItemClick(item.id)}
                  title={isCollapsed ? item.label : undefined}
                >
                  {item.icon && <span className="item-icon">{item.icon}</span>}
                  {!isCollapsed && (
                    <>
                      <span className="item-label flex-1">{item.label}</span>
                    </>
                  )}
                </div>
              ) : (
                (() => {
                  const depth = (item.id.match(/\//g) || []).length;
                  const indentClass = depth === 2 ? 'pl-10 text-sm' : depth === 1 ? 'pl-6 text-sm' : '';
                  return (
                    <div
                      className={`sidebar-item ${activeSection === item.id ? 'active' : ''} ${indentClass}`}
                      onClick={() => handleItemClick(item.id)}
                      title={isCollapsed ? item.label : undefined}
                    >
                      {item.icon && <span className="item-icon">{item.icon}</span>}
                      {!isCollapsed && (
                        <>
                          <span className="item-label flex-1">{item.label}</span>
                          {typeof item.count === 'number' && (
                            <span className="opacity-70 text-xs">{item.count}</span>
                          )}
                        </>
                      )}
                    </div>
                  );
                })()
              )}
            </li>
          ))}
          <li className="sidebar-section">
            <button
              className="sidebar-toggle"
              onClick={() => onToggleCollapse(!isCollapsed)}
              title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {isCollapsed ? <ChevronRightCircle size={24} /> : <ChevronLeftCircle size={24} />}
            </button>
          </li>
        </ul>
      </nav>
    </aside>
  );
};

export default Sidebar; 